import { useDisableScroll } from '@toggle/helpers/src/hooks/use-disable-scroll/useDisableScroll';
import { isElementScrollbarVisible } from '@toggle/helpers/src/utils/dom/DOM';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';

import {
  AssetDetails,
  EarningState,
  Expectation,
  Scenario,
} from '@/api/asset/asset-schema';
import { useTouchDevice } from '@/stores/MediaQueryStore';
import { formatEarning, getEarningState } from '@/utils/asset/assetUtils';
import { routes } from '@/utils/page-routes';

import { PastEarningsTable } from '../past-earnings-table/PastEarningsTable';
import { PastEpisodesCard } from './past-episodes-card/PastEpisodesCard';
import * as S from './PastEarningsDrawer.styles';

export const translationKeys = {
  [Expectation.Beat]: {
    titleKey: 'earnings:drawer.titleScheduledBeat',
    subtitleKey: 'earnings:drawer.subtitleBeat',
    testId: 'drawer-scheduled-beat',
  },
  [Expectation.Miss]: {
    titleKey: 'earnings:drawer.titleScheduledMiss',
    subtitleKey: 'earnings:drawer.subtitleMiss',
    testId: 'drawer-scheduled-miss',
  },
  [Expectation.Meet]: {
    titleKey: '',
    subtitleKey: '',
    testId: '',
  },
  [EarningState.Beat]: {
    titleKey: 'earnings:drawer.titleReportedBeat',
    subtitleKey: 'earnings:drawer.subtitleBeat',
    testId: 'drawer-reported-beat',
  },
  [EarningState.Miss]: {
    titleKey: 'earnings:drawer.titleReportedMiss',
    subtitleKey: 'earnings:drawer.subtitleMiss',
    testId: 'drawer-reported-miss',
  },
  [EarningState.Meet]: {
    titleKey: 'earnings:drawer.titleReportedMeet',
    subtitleKey: 'earnings:drawer.subtitleMeet',
    testId: 'drawer-reported-meet',
  },
};

const getTranslationsKeys = (
  assetDetails: AssetDetails,
  earningState?: EarningState,
  expectation?: Expectation
) => {
  if (!assetDetails.reported && expectation) {
    return translationKeys[expectation];
  }

  if (assetDetails.reported && earningState) {
    return translationKeys[earningState];
  }

  return {
    titleKey: '',
    subtitleKey: '',
    testId: '',
  };
};

interface PastEarningsDrawerProps {
  assetDetails: AssetDetails;
  scenario?: Scenario;
  ticker: string;
  expectation: Expectation;
  isOpen: boolean;
  closeDrawer: () => void;
}

export const EarningsDrawer = ({
  assetDetails,
  scenario,
  expectation,
  isOpen,
  closeDrawer,
}: PastEarningsDrawerProps) => {
  const router = useRouter();
  const { t } = useTranslation(['earnings']);
  const isTouchDevice = useTouchDevice();
  const tableContainerRef = useRef<HTMLElement>(null);
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);

  useDisableScroll(isOpen, 'auto');

  useEffect(() => {
    const hasScroll = isElementScrollbarVisible(tableContainerRef);
    setIsScrollbarVisible(hasScroll);
  }, [isOpen]);

  const valueFormatted = formatEarning(assetDetails.eps, assetDetails.currency);
  const earningState = getEarningState(assetDetails.eps);

  const { titleKey, subtitleKey, testId } = getTranslationsKeys(
    assetDetails,
    earningState,
    expectation
  );

  return (
    <section data-testid="past-earnings-drawer">
      <S.StyledDrawer
        data-testid={testId}
        isOpen={isOpen}
        handleCloseClick={closeDrawer}
        drawerWidth={
          router.asPath.includes(routes.layoutSimple) ? '100%' : '600px'
        }
        headerTitle={
          titleKey &&
          t(titleKey, {
            amount: valueFormatted,
          })
        }
      >
        {isOpen && (
          <>
            {subtitleKey && (
              <S.DrawerText>
                <Trans
                  i18nKey={subtitleKey}
                  components={[<S.Strong key="highlight" />]}
                  values={{
                    ticker: assetDetails.ticker,
                    exchange: assetDetails.exchange,
                    companyName: assetDetails.company_name,
                  }}
                />
              </S.DrawerText>
            )}
            {isTouchDevice && scenario?.history?.meta && (
              <PastEpisodesCard metaData={scenario.history.meta} />
            )}
            <S.Container
              data-testid="past-earning-table-container"
              ref={tableContainerRef}
            >
              {scenario?.history && (
                <PastEarningsTable
                  currency={assetDetails.currency}
                  history={scenario.history}
                  isScrollbarVisible={isScrollbarVisible}
                  reported={assetDetails.reported}
                />
              )}
            </S.Container>
          </>
        )}
      </S.StyledDrawer>
    </section>
  );
};

export const PastEarningsDrawer = dynamic({
  ssr: false,
  loader: () => Promise.resolve(EarningsDrawer),
});
